import { format } from 'date-fns';
import { TranslateFn, useTranslate } from 'hooks/useTranslate';
import { FC } from 'react';

import { dateFormat } from 'constants/formats';
import DataTablePagination from 'modules/_shared/components/Tables/DataTablePagination';
import EmptyStateForTable from 'modules/_shared/components/EmptyStates/EmptyStateForTable';
import { BeneficiaryWithEnvelopeId } from 'modules/beneficiaries/hooks/useGetBeneficiariesPendingEnvelopes';
import {
  EnvelopeStatus,
  envelopeStatus,
} from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';

type BeneficiariesPendingSignatureTableProps = {
  paginatedBeneficiaries: BeneficiaryWithEnvelopeId[];
  startIndex: number;
  filteredCount: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (_page: number) => void; // eslint-disable-line no-unused-vars
  onItemsPerPageChange: (_newItemsPerPage: number) => void; // eslint-disable-line no-unused-vars
  resendingBeneficiaries: Set<string>;
};

const getBadgeColor = (envelopeStatus: string) => {
  if (envelopeStatus === EnvelopeStatus.VOIDED) {
    return 'badge-warning';
  }
  if (
    envelopeStatus === EnvelopeStatus.SENT ||
    envelopeStatus === EnvelopeStatus.DELIVERED
  ) {
    return 'badge-success';
  }
  return 'badge-danger';
};

const renderEnvelopeStatus = (
  beneficiary: BeneficiaryWithEnvelopeId,
  isResending: boolean,
  t: TranslateFn
) => {
  if (isResending) {
    return (
      <span className="badge badge-dot badge-info">
        <em className="icon ni ni-loader" />
        {t('Updating')}
      </span>
    );
  }
  if (beneficiary?.envelopeId) {
    const badgeColor = getBadgeColor(beneficiary?.envelopeStatus);

    return (
      <span className={`badge badge-dot ${badgeColor}`}>
        {t(
          envelopeStatus[
            beneficiary?.envelopeStatus as keyof typeof envelopeStatus
          ].label
        ).toUpperCase()}
      </span>
    );
  }
  return (
    <span className="badge badge-dot badge-danger">
      {t('NotAvailable').toUpperCase()}
    </span>
  );
};

const BeneficiariesPendingSignatureTable: FC<
  BeneficiariesPendingSignatureTableProps
> = ({
  paginatedBeneficiaries,
  startIndex,
  filteredCount,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  resendingBeneficiaries,
}) => {
  const { t } = useTranslate();

  return (
    <>
      <table className="table ">
        <thead>
          <tr className="tb-tnx-head">
            <th className="nk-tb-col p-3">
              <span>Nº</span>
            </th>
            <th className="nk-tb-col p-3">
              <span>{t('Name')}</span>
            </th>
            <th className="nk-tb-col p-3">
              <span>{t('E-Mail')}</span>
            </th>
            <th className="nk-tb-col p-3">
              <span>{t('Plan')}</span>
            </th>
            <th className="nk-tb-col p-3 text-center">
              <span>{t('StartDate')}</span>
            </th>
            <th className="nk-tb-col p-3">
              <span>{t('SignatureStatus')}</span>
            </th>
            <th className="nk-tb-col p-3">
              <span>{t('EnvelopeStatus')}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {paginatedBeneficiaries.map((beneficiary, index) => {
            const isResending = resendingBeneficiaries.has(
              beneficiary._id || ''
            );
            return (
              <tr
                className={`tb-tnx-item ${isResending ? 'opacity-50' : ''}`}
                key={beneficiary._id}
              >
                <td className="tb-tnx-id">
                  <span>{startIndex + index + 1}</span>
                </td>
                <td className="tb-tnx-info mw-200px">
                  <span className="tb-tnx-info w-100 overflow-hidden">
                    {beneficiary.name || '-'}
                  </span>
                </td>
                <td className="tb-tnx-info">
                  <span className="tb-tnx-info w-100">
                    {beneficiary.email || '-'}
                  </span>
                </td>
                <td className="tb-tnx-info">
                  <span className="tb-tnx-info w-100 text-ellipsis">
                    {beneficiary.planName || '-'}
                  </span>
                </td>
                <td className="tb-tnx-info text-center">
                  <span className="tb-tnx-info w-100">
                    {format(new Date(beneficiary.planStartDate), dateFormat) ||
                      '-'}
                  </span>
                </td>
                <td className="tb-tnx-info">
                  {isResending ? (
                    <span className="badge badge-dot badge-info">
                      <em className="icon ni ni-loader" />
                      {t('Resending')}
                    </span>
                  ) : (
                    <span className="badge badge-dot badge-warning">
                      {t(beneficiary?.invitationStatus || '')}
                    </span>
                  )}
                </td>
                <td className="tb-tnx-info">
                  {renderEnvelopeStatus(beneficiary, isResending, t)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredCount > 0 ? (
        <DataTablePagination
          className="bg-white"
          currentPage={currentPage}
          totalItems={filteredCount}
          itemPerPage={itemsPerPage}
          paginate={onPageChange}
          onChangeRowsPerPage={onItemsPerPageChange}
          customItemPerPage={itemsPerPage}
          setRowsPerPage={onItemsPerPageChange}
        />
      ) : (
        <EmptyStateForTable
          mainMessage={t('NoPendingSignatureWithFilter')}
          infoMessage={t('NoPendingSignatureWithFilterDescription')}
          colspan={7}
        />
      )}
    </>
  );
};

export default BeneficiariesPendingSignatureTable;
